import { useMutation, useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FeedPostCard from '../../components/FeedPostCard';
import ReportPostDialog from '../../components/ReportPost/ReportPostDialog';
import ShareStoryModal from '../../components/ShareStory/ShareStoryModal';
import { useAuth } from '../../context/AuthContext';
import { TOGGLE_ACCOUNT_BLOCK } from '../../graphql/resolvers/feed/feedMutations/toggleAccountBlock.gql';
import { TOGGLE_AUTHOR_MUTE } from '../../graphql/resolvers/feed/feedMutations/toggleAuthorMute.gql';
import { TOGGLE_THREAD_MUTE } from '../../graphql/resolvers/feed/feedMutations/toggleThreadMute.gql';
import { GET_USER_INTERACTION_FEED } from '../../graphql/resolvers/feed/feedQueries/getUserInteractionFeed.gql';
import { usePostInteractions } from '../../hooks/usePostInteractions';
import { FeedPost } from '../../interfaces/feed.interface';

type InteractionType = 'LIKED' | 'BOOKMARKED' | 'REPOSTED';

interface Props {
  kind: InteractionType;
  title: string;
  emptyMessage: string;
}

const UserInteractionFeed: React.FC<Props> = ({
  kind,
  title,
  emptyMessage,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allPosts, setAllPosts] = useState<FeedPost[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  // Posts the viewer optimistically removed (un-like / un-bookmark / mute / block).
  // Kept in a ref so the cache-and-network data sync below doesn't re-add them when
  // getUserInteractionFeed re-emits after a Post cache write (TRIL-261 review).
  const removedIdsRef = useRef<Set<string>>(new Set());
  const isRemoved = useCallback(
    (p: FeedPost) =>
      removedIdsRef.current.has(p.id) ||
      (!!p.postId && removedIdsRef.current.has(p.postId)),
    [],
  );

  const { toggleLike, toggleBookmark } = usePostInteractions();
  const [toggleThreadMuteMutation] = useMutation(TOGGLE_THREAD_MUTE);
  const [toggleAccountBlockMutation] = useMutation(TOGGLE_ACCOUNT_BLOCK);
  const [toggleAuthorMuteMutation] = useMutation(TOGGLE_AUTHOR_MUTE);

  const [shareModal, setShareModal] = useState<{
    open: boolean;
    storyId: string;
    storyTitle: string;
    storyUrl: string;
  }>({ open: false, storyId: '', storyTitle: '', storyUrl: '' });
  const [reportDialog, setReportDialog] = useState<{
    open: boolean;
    postId: string;
  }>({ open: false, postId: '' });

  const { data, loading, error, fetchMore } = useQuery(
    GET_USER_INTERACTION_FEED,
    {
      variables: {
        userId: user?.id,
        interaction: kind,
        cursor: null,
        limit: 20,
      },
      skip: !user?.id,
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (data?.getUserInteractionFeed?.posts) {
      setAllPosts(
        data.getUserInteractionFeed.posts.filter(
          (p: FeedPost) => !isRemoved(p),
        ),
      );
      setCursor(data.getUserInteractionFeed.pageInfo?.endCursor || null);
    }
  }, [data, isRemoved]);

  const loadMore = useCallback(async () => {
    if (!cursor || loading) return;

    const result = await fetchMore({
      variables: {
        userId: user?.id,
        interaction: kind,
        cursor,
        limit: 20,
      },
    });

    if (result.data?.getUserInteractionFeed?.posts) {
      setAllPosts((prev) => [
        ...prev,
        ...result.data.getUserInteractionFeed.posts.filter(
          (p: FeedPost) => !isRemoved(p),
        ),
      ]);
      setCursor(result.data.getUserInteractionFeed.pageInfo?.endCursor || null);
    }
  }, [cursor, loading, fetchMore, kind, user?.id, isRemoved]);

  // Surface-aware removal: an un-like on the Likes feed (or un-bookmark on the
  // Bookmarks feed) drops the post out of the list it no longer belongs to.
  // Unlike RecommendationFeed, removal is from the local `allPosts` state, not
  // the getUserFeed Apollo cache (this page maintains its own list state).
  const removeFromList = useCallback((postId: string) => {
    removedIdsRef.current.add(postId);
    setAllPosts((prev) =>
      prev.filter((x) => x.id !== postId && x.postId !== postId),
    );
  }, []);

  const filterAuthorFromList = useCallback(
    (authorId: string | null) =>
      setAllPosts((prev) =>
        prev.filter((x) => {
          const isAuthor = (x.author?.authorId ?? x.userId) === authorId;
          if (isAuthor && x.id) removedIdsRef.current.add(x.id);
          return !isAuthor;
        }),
      ),
    [],
  );

  const handleToggleLike = useCallback(
    async (post: FeedPost) => {
      const postId = post.postId ?? post.id;
      const wasLiked = !!post.viewerInteractions?.hasLiked;
      const ok = await toggleLike(post);
      // Only drop from the list if the un-like actually persisted; otherwise the
      // cache rolls back to liked and the post would wrongly vanish (TRIL-261 review).
      if (ok && kind === 'LIKED' && wasLiked) removeFromList(postId);
    },
    [toggleLike, kind, removeFromList],
  );

  const handleToggleBookmark = useCallback(
    async (post: FeedPost) => {
      const postId = post.postId ?? post.id;
      const wasBookmarked = !!post.viewerInteractions?.hasBookmarked;
      const ok = await toggleBookmark(post);
      if (ok && kind === 'BOOKMARKED' && wasBookmarked) removeFromList(postId);
    },
    [toggleBookmark, kind, removeFromList],
  );

  const handleCopyLink = useCallback((postId: string) => {
    void navigator.clipboard?.writeText(
      `${window.location.origin}/post/${postId}`,
    );
  }, []);

  // No reply drawer on this page — navigate to the post detail to reply.
  const handleReply = useCallback(
    (postId: string) => {
      navigate(`/post/${postId}`);
    },
    [navigate],
  );

  // TRIL-583 — same destination as handleReply, but asks the post to open the
  // replies drawer rather than the composer.
  const handleViewReplies = useCallback(
    (postId: string) => {
      navigate(`/post/${postId}`, { state: { openReplies: true } });
    },
    [navigate],
  );

  const handleOpenShare = useCallback(
    (postId: string, postTitle: string, _post: FeedPost) => {
      setShareModal({
        open: true,
        storyId: postId,
        storyTitle: postTitle,
        storyUrl: `/post/${postId}`,
      });
    },
    [],
  );

  const handleMuteThread = useCallback(
    async (post: FeedPost) => {
      const rootPostId = post.rootPostId ?? post.postId ?? post.id;
      if (!rootPostId) return;
      try {
        await toggleThreadMuteMutation({ variables: { rootPostId } });
        removeFromList(post.postId ?? post.id);
      } catch (err) {
        console.error('toggleThreadMute failed', err);
      }
    },
    [toggleThreadMuteMutation, removeFromList],
  );

  const handleMuteAccount = useCallback(
    async (authorId: string | null) => {
      if (!authorId) return;
      try {
        await toggleAuthorMuteMutation({ variables: { authorId } });
        filterAuthorFromList(authorId);
      } catch (err) {
        console.error('toggleAuthorMute failed', err);
      }
    },
    [toggleAuthorMuteMutation, filterAuthorFromList],
  );

  const handleBlockAccount = useCallback(
    async (authorId: string | null) => {
      if (!authorId) return;
      try {
        await toggleAccountBlockMutation({
          variables: { accountId: authorId },
        });
        filterAuthorFromList(authorId);
      } catch (err) {
        console.error('toggleAccountBlock failed', err);
      }
    },
    [toggleAccountBlockMutation, filterAuthorFromList],
  );

  const handleOpenReport = useCallback((postId: string) => {
    setReportDialog({ open: true, postId });
  }, []);

  if (!user) {
    return <div className="interaction-feed-page">Please log in.</div>;
  }

  if (loading && allPosts.length === 0) {
    return <div className="interaction-feed-page">Loading {title}…</div>;
  }

  if (error) {
    return (
      <div className="interaction-feed-page">
        Error loading {title}: {error.message}
      </div>
    );
  }

  return (
    <div className="interaction-feed-page">
      <h2>{title}</h2>

      {allPosts.length === 0 ? (
        <p>{emptyMessage}</p>
      ) : (
        <>
          {allPosts.map((p) => (
            <FeedPostCard
              key={p.id}
              post={p}
              userId={user.id}
              username={user.username}
              onToggleLike={handleToggleLike}
              onToggleBookmark={handleToggleBookmark}
              onReply={handleReply}
              onViewReplies={handleViewReplies}
              onCopyLink={handleCopyLink}
              onOpenShare={handleOpenShare}
              onMuteThread={handleMuteThread}
              onMuteAccount={handleMuteAccount}
              onBlockAccount={handleBlockAccount}
              onOpenReport={handleOpenReport}
            />
          ))}

          {cursor && (
            <button type="button" disabled={loading} onClick={loadMore}>
              {loading ? 'Loading…' : 'Load more'}
            </button>
          )}
        </>
      )}

      <ShareStoryModal
        open={shareModal.open}
        onClose={() => setShareModal((prev) => ({ ...prev, open: false }))}
        storyId={shareModal.storyId}
        storyTitle={shareModal.storyTitle}
        storyUrl={shareModal.storyUrl}
      />

      <ReportPostDialog
        open={reportDialog.open}
        onClose={() => setReportDialog({ open: false, postId: '' })}
        postId={reportDialog.postId}
      />
    </div>
  );
};

export default UserInteractionFeed;
